import {all, call, put, takeLatest} from '@redux-saga/core/effects';
import {push} from 'connected-react-router';
import {acceptLegalTerms, getLegalDocumentText} from 'Core/api/legalAcceptanceApi';
import {
  acceptAgreements,
  getLegalAgreementsText,
  LegalAcceptanceActionType,
  LegalAgreementDictionary,
  loadLegalAgreementsText,
} from 'Core/ducks/legalAcceptance';
import {AsyncResult, fromResult, Loading} from 'Core/models/Async';
import {LegalDocumentText} from 'Core/models/LegalDocument';
import CoreRouteConstants from 'Core/CoreRouteConstants';
import * as E from 'fp-ts/lib/Either';
import {refreshToken} from 'Shared/utilities/Auth';
import * as O from 'fp-ts/lib/Option';
import {pipe} from 'fp-ts/lib/pipeable';
import {getUserClaims} from 'Shared/utilities/UserProfileUtilities';
import {findFirst} from 'fp-ts/lib/Array';
import {head} from 'fp-ts/lib/Array';
import {getIsNewYorkFromAgency} from 'Shared/utilities/isNewYorkUtility';
import {setSelectedAgency, setUserClaims} from 'Users/ducks/userProfile';
import {UserTypeClaimKey, UserTypeClaims} from 'Users/models/Claims';
import {eq} from 'Shared/utilities/equals';

function* getDocument(id: string) {
  const legalTextEither: AsyncResult<LegalDocumentText> = yield call(getLegalDocumentText, id);

  if (E.isRight(legalTextEither)) {
    return {
      [id]: legalTextEither.right.legalText,
    };
  } else {
    return null;
  }
}

export function* getLegalAgreementFromApi(action: ReturnType<typeof getLegalAgreementsText>) {
  const legalTexts: LegalAgreementDictionary[] = yield all(action.payload.ids.map(getDocument));

  const textDictionary = legalTexts.reduce(Object.assign, {});

  yield put(loadLegalAgreementsText(fromResult(textDictionary)));
}

export function* getLegalAcceptanceTextSaga() {
  yield takeLatest(LegalAcceptanceActionType.GET_LEGAL_AGREEMENT_TEXT, getLegalAgreementFromApi);
}

export function* acceptLegalAgreements(action: ReturnType<typeof acceptAgreements>) {
  yield put(loadLegalAgreementsText(new Loading()));

  yield call(acceptLegalTerms, action.payload.userId, action.payload.ids);
  yield call(refreshToken);

  const newClaims = pipe(getUserClaims());

  if (O.isSome(newClaims)) {
    const agentClaims = pipe(
      newClaims.value.userTypes,
      findFirst(eq('$type', UserTypeClaimKey.AgentSite)),
      O.chain(
        UserTypeClaims.match({
          [UserTypeClaimKey.AgentSite]: agentClaims => {
            const impersonatedAgentClaims = O.fromNullable(agentClaims.impersonatedAgentClaim);
            return O.isSome(impersonatedAgentClaims) ? impersonatedAgentClaims : O.some(agentClaims);
          },
          default: () => O.none,
        })
      )
    );

    if (O.isSome(agentClaims)) {
      const defaultAgency = pipe(agentClaims.value.agencyAssociations, head);

      const isNewYork = getIsNewYorkFromAgency(defaultAgency);
      yield put(setSelectedAgency(defaultAgency, isNewYork));
    }
    yield put(setUserClaims(fromResult(newClaims.value)));
  }
  yield put(push(CoreRouteConstants.DASHBOARD));
}

export function* acceptLegalAgreementsSaga() {
  yield takeLatest(LegalAcceptanceActionType.ACCEPT, acceptLegalAgreements);
}

export default function*() {
  yield all([getLegalAcceptanceTextSaga(), acceptLegalAgreementsSaga()]);
}
